import {
  inject,
  provide,
  computed,
  watch,
  onMounted,
  onUnmounted,
  unref,
  type ComputedRef,
  type Ref,
} from 'vue';

export type ItpFormCtx = {
  id: string;
  r$: any;
  disabled: boolean | { value: boolean };
  readonly: boolean | { value: boolean };
  labelAlign?: ComputedRef<'left' | 'right' | undefined>;
  state?: Record<string, any>;
  rules: ComputedRef<Record<string, any>>;
  errorsOf(field: string): string[];
  isRequired(field: string): boolean;
  touchField(field: string): void;
  registerField(
    name: string,
    rules: Record<string, any>,
    initialValue?: any,
  ): void;
  unregisterField(name: string): void;
  updateFieldValue(name: string, value: any): void;
};

const KEY = Symbol('itp-form');
const GROUP_REQUIRED_KEY = Symbol('itp-group-required');

export function provideItpFormContext(ctx: ItpFormCtx) {
  provide(KEY, ctx);
}
export function useItpFormContext(): ItpFormCtx | null {
  return inject<ItpFormCtx | null>(KEY, null);
}

export function provideGroupRequired(isRequired: ComputedRef<boolean>) {
  provide(GROUP_REQUIRED_KEY, isRequired);
}

export function useItpFormField(
  props: { name: string; modelValue?: any; required?: boolean },
  buildRules: (effectiveRequired: boolean) => Record<string, any>,
) {
  const form = useItpFormContext();

  const groupRequired = inject<ComputedRef<boolean> | Ref<boolean> | null>(
    GROUP_REQUIRED_KEY,
    null,
  );
  const effectiveRequired = computed(
    () => !!props.required || !!groupRequired?.value,
  );

  const currentRules = () => buildRules(effectiveRequired.value);

  onMounted(() => {
    if (form) {
      form.registerField(props.name, currentRules(), props.modelValue);
    }
  });

  onUnmounted(() => {
    if (form) {
      form.unregisterField(props.name);
    }
  });

  watch(effectiveRequired, () => {
    if (form) {
      form.registerField(props.name, currentRules());
    }
  });

  watch(
    () => props.modelValue,
    (newValue) => {
      if (form) {
        form.updateFieldValue(props.name, newValue);
      }
    },
  );

  function touch() {
    if (form) {
      form.touchField(props.name);
    }
  }

  return { form, touch, effectiveRequired };
}

export function useItpFormControl(props: {
  readonly?: boolean;
  disabled?: boolean;
}) {
  const form = useItpFormContext();

  const formDisabled = computed(() => {
    const disabled = form?.disabled as any;
    return typeof disabled === 'object' ? !!disabled?.value : !!disabled;
  });

  const formReadonly = computed(() => {
    const readonly = form?.readonly as any;
    return typeof readonly === 'object' ? !!readonly?.value : !!readonly;
  });

  const isDisabled = computed(() => !!props.disabled || formDisabled.value);
  const isReadonly = computed(() => !!props.readonly || formReadonly.value);
  const isReadonlyOrDisabled = computed(
    () => isReadonly.value || isDisabled.value,
  );

  return { isReadonly, isDisabled, isReadonlyOrDisabled };
}

export function useItpFormValidation(fieldNames: string | string[]) {
  const form = useItpFormContext();
  const names = Array.isArray(fieldNames) ? fieldNames : [fieldNames];

  const isRequired = computed(() => {
    const allRules = form?.rules?.value;
    return names.some((field) => {
      const fieldRules = allRules?.[field];
      if (!fieldRules || typeof fieldRules !== 'object') {
        return false;
      }
      return !!(fieldRules as any).required || !!(fieldRules as any).accepted;
    });
  });

  const errors = computed<string[]>(() =>
    names.flatMap((field) => form?.errorsOf(field) ?? []),
  );

  const isInvalid = computed(() => errors.value.length > 0);
  const state = computed<true | false | undefined>(() =>
    isInvalid.value ? false : undefined,
  );

  return { isRequired, errors, isInvalid, state };
}

export function useItpFormLogic(
  r$: any,
  formState: any,
  props: { disabled?: boolean; readonly?: boolean },
  emit: (event: 'submit', payload: any) => void,
  mergedRules?: ComputedRef<Record<string, any>> | Ref<Record<string, any>>,
) {
  const submittable = computed(() => {
    if (props.disabled || props.readonly) {
      return false;
    }

    if (mergedRules) {
      const rules = unref(mergedRules);
      const state = unref(formState);
      if (rules && state) {
        for (const [fieldName, fieldRules] of Object.entries(rules)) {
          if (fieldRules && typeof fieldRules === 'object') {
            if ((fieldRules as any).required || (fieldRules as any).accepted) {
              const val = state[fieldName];
              if (val === null || val === undefined || val === '') {
                return false;
              }
            }
          }
        }
      }
    }

    return true;
  });

  const reset = () => {
    if (formState.value && typeof formState.value === 'object') {
      for (const key of Object.keys(formState.value)) {
        formState.value[key] = null;
      }
    }
    r$.$reset();
  };

  const submit = async () => {
    if (mergedRules) {
      const rules = unref(mergedRules);
      const state = unref(formState);
      if (rules && state) {
        for (const [fieldName, fieldRules] of Object.entries(rules)) {
          if (fieldRules && typeof fieldRules === 'object') {
            if ((fieldRules as any).required || (fieldRules as any).accepted) {
              const val = state[fieldName];
              if (val === null || val === undefined || val === '') {
                console.warn(
                  `[ItpForm] Submit blocked: required field "${fieldName}" is empty`,
                );
                return;
              }
            }
          }
        }
      }
    }

    emit('submit', unref(formState));
  };

  const handleSubmit = submit;

  return {
    submittable,
    reset,
    submit,
    handleSubmit,
  };
}
